(() => {
  if (customElements.get('ns-delivery-estimate')) return;

  const holidayCache = new Map();
  const transitDays = { mainland: [1, 2], balearic: [3, 5], other: [5, 10] };
  const dayKey = (date) => date.toISOString().slice(0, 10);

  class NsDeliveryEstimate extends HTMLElement {
    static todayInMadrid(now = new Date()) {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit',
      }).formatToParts(now);
      const value = (type) => Number(parts.find((part) => part.type === type).value);
      return new Date(Date.UTC(value('year'), value('month') - 1, value('day'), 12));
    }

    static easterSunday(year) {
      const a = year % 19;
      const b = Math.floor(year / 100);
      const c = year % 100;
      const d = Math.floor(b / 4);
      const e = b % 4;
      const f = Math.floor((b + 8) / 25);
      const g = Math.floor((b - f + 1) / 3);
      const h = (19 * a + b - d - g + 15) % 30;
      const i = Math.floor(c / 4);
      const k = c % 4;
      const l = (32 + 2 * e + 2 * i - h - k) % 7;
      const m = Math.floor((a + 11 * h + 22 * l) / 451);
      const month = Math.floor((h + l - 7 * m + 114) / 31);
      const day = ((h + l - 7 * m + 114) % 31) + 1;
      return new Date(Date.UTC(year, month - 1, day, 12));
    }

    static nationalHolidays(year) {
      if (!holidayCache.has(year)) {
        const fixed = ['01-01', '01-06', '05-01', '08-15', '10-12', '11-01', '12-06', '12-08', '12-25'];
        const dates = new Set(fixed.map((day) => `${year}-${day}`));
        const goodFriday = this.easterSunday(year);
        goodFriday.setUTCDate(goodFriday.getUTCDate() - 2);
        dates.add(dayKey(goodFriday));
        holidayCache.set(year, dates);
      }
      return holidayCache.get(year);
    }

    static isWorkingDay(date) {
      const day = date.getUTCDay();
      return day !== 0 && day !== 6 && !this.nationalHolidays(date.getUTCFullYear()).has(dayKey(date));
    }

    static addWorkingDays(from, count) {
      const date = new Date(from.getTime());
      let remaining = count;
      while (remaining > 0) {
        date.setUTCDate(date.getUTCDate() + 1);
        if (this.isWorkingDay(date)) remaining -= 1;
      }
      return date;
    }

    static estimateFor(now, destination = 'mainland') {
      const purchase = this.todayInMadrid(now);
      const dispatch = this.addWorkingDays(purchase, 1);
      const [minimum, maximum] = transitDays[destination] || transitDays.mainland;
      return {
        purchase, dispatch,
        earliest: this.addWorkingDays(dispatch, minimum),
        latest: this.addWorkingDays(dispatch, maximum),
        minimum, maximum,
      };
    }

    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      if (!this.querySelector('[data-ns-delivery-purchase]')) return;
      const options = { signal: this.abortController.signal };
      this.language = this.dataset.language === 'en' ? 'en' : 'es';
      this.locale = this.language === 'en' ? 'en-GB' : 'es-ES';
      this.monthFormat = new Intl.DateTimeFormat(this.locale, { month: 'short', timeZone: 'UTC' });
      this.weekdayFormat = new Intl.DateTimeFormat(this.locale, { weekday: 'long', timeZone: 'UTC' });
      this.activeStep = null;
      this.classList.toggle('is-paused', document.hidden);
      this.querySelectorAll('[data-ns-delivery-step]').forEach((button) => {
        button.disabled = false;
        button.addEventListener('click', () => this.selectStep(button.dataset.nsDeliveryStep, true), options);
      });
      if ('IntersectionObserver' in window) {
        this.entryObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            this.classList.toggle('is-visible', entry.isIntersecting);
          }
        }, { threshold: .15 });
        this.entryObserver.observe(this);
      } else {
        this.classList.add('is-visible');
      }
      document.addEventListener('visibilitychange', () => {
        this.classList.toggle('is-paused', document.hidden);
        window.clearTimeout(this.refreshTimer);
        if (!document.hidden) {
          this.updateEstimate(false);
          this.scheduleRefresh();
        }
      }, options);
      this.updateEstimate(false);
      this.scheduleRefresh();
    }

    shortDate(date, includeYear = false) {
      const month = this.monthFormat.format(date).replace(/\.$/, '');
      return `${date.getUTCDate()} ${month}${includeYear ? ` ${date.getUTCFullYear()}` : ''}`;
    }

    dateRange(start, end) {
      const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
      const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();
      if (dayKey(start) === dayKey(end)) return this.shortDate(start);
      if (sameMonth) {
        return `${start.getUTCDate()}–${end.getUTCDate()} ${this.monthFormat.format(end).replace(/\.$/, '')}`;
      }
      return `${this.shortDate(start, !sameYear)} – ${this.shortDate(end, !sameYear)}`;
    }

    updateEstimate(announce = false) {
      if (this.dataset.stockPending === 'true') {
        this.stepMessages = {purchase: this.dataset.pendingMessage, dispatch: this.dataset.pendingMessage, arrival: this.dataset.pendingMessage};
        this.dataset.calendarDay = dayKey(NsDeliveryEstimate.todayInMadrid());
        this.setAttribute('data-ns-delivery-ready', '');
        this.selectStep(this.activeStep);
        return;
      }
      const estimate = NsDeliveryEstimate.estimateFor(new Date(), 'mainland');
      const range = this.dateRange(estimate.earliest, estimate.latest);
      const earliestDay = this.weekdayFormat.format(estimate.earliest);
      const latestDay = this.weekdayFormat.format(estimate.latest);
      const dateRange = `${earliestDay} ${this.shortDate(estimate.earliest)} – ${latestDay} ${this.shortDate(estimate.latest)}`;
      this.stepMessages = {
        purchase: this.dataset.purchaseMessage.replace('__DATE__', this.shortDate(estimate.purchase)),
        dispatch: this.dataset.dispatchMessage.replace('__DATE__', `${this.weekdayFormat.format(estimate.dispatch)} ${this.shortDate(estimate.dispatch)}`),
        arrival: this.dataset.arrivalMessage.replace('__DATE__', dateRange),
      };
      const purchase = this.querySelector('[data-ns-delivery-purchase]');
      const dispatch = this.querySelector('[data-ns-delivery-dispatch]');
      purchase.textContent = `${this.dataset.todayLabel}, ${this.shortDate(estimate.purchase)}`;
      purchase.dateTime = dayKey(estimate.purchase);
      dispatch.textContent = this.shortDate(estimate.dispatch);
      dispatch.dateTime = dayKey(estimate.dispatch);
      this.querySelector('[data-ns-delivery-range]').textContent = range;
      this.querySelector('[data-ns-delivery-range]').setAttribute('aria-label', dateRange);
      this.querySelector('[data-ns-delivery-arrival]').textContent = range;
      this.dataset.calendarDay = dayKey(estimate.purchase);
      this.setAttribute('data-ns-delivery-ready', '');
      this.selectStep(this.activeStep);
      if (announce) {
        const destinationLabel = this.dataset.countryLabel || '';
        this.querySelector('[data-ns-delivery-status]').textContent = `${destinationLabel}. ${this.dataset.estimatedLabel}: ${range}.`;
      }
    }

    selectStep(step, toggle = false) {
      if (step && !this.stepMessages?.[step]) return;
      this.activeStep = toggle && this.activeStep === step ? null : step;
      this.querySelectorAll('[data-ns-delivery-step]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.nsDeliveryStep === this.activeStep));
      });
      const detail = this.querySelector('[data-ns-delivery-step-detail]');
      detail.hidden = !this.activeStep;
      detail.textContent = this.activeStep ? this.stepMessages[this.activeStep] : '';
    }

    scheduleRefresh() {
      window.clearTimeout(this.refreshTimer);
      if (document.hidden) return;
      this.refreshTimer = window.setTimeout(() => {
        const today = dayKey(NsDeliveryEstimate.todayInMadrid());
        if (today !== this.dataset.calendarDay) this.updateEstimate(false);
        this.scheduleRefresh();
      }, 60000);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.entryObserver?.disconnect();
      window.clearTimeout(this.refreshTimer);
    }
  }

  customElements.define('ns-delivery-estimate', NsDeliveryEstimate);
})();
